import JSZip from 'jszip';

import { georeferenceFromLayer, latLngToCadPoint } from '../lib/cad/cadImport';
import type { SiteLayer } from '../types/site';

const DXF_LINE_ENDING = '\r\n';

function pair(code: number, value: string | number): string[] {
  return [String(code), String(value)];
}

function dxfNumber(value: number): string {
  if (!Number.isFinite(value)) throw new Error('CAD export encountered a non-finite coordinate.');
  return Number(value.toFixed(8)).toString();
}

function dxfLayerName(value: unknown): string {
  const cleaned = String(value ?? 'EDITED').replace(/[<>/\\":;?*|=,\r\n]/g, '_').trim();
  return (cleaned || 'EDITED').slice(0, 255);
}

function outputBaseName(value: unknown): string {
  const file = String(value ?? 'cad-overlay').replace(/\.dwg$/i, '');
  const cleaned = file.replace(/[<>/\\":;?*|=,\r\n]/g, '_').trim();
  return cleaned || 'cad-overlay';
}

function fallbackInsUnitsForMetersPerUnit(metersPerUnit: number): number {
  if (Math.abs(metersPerUnit - 0.3048) < 1e-8) return 2;
  if (Math.abs(metersPerUnit - 1200 / 3937) < 1e-8) return 21;
  if (Math.abs(metersPerUnit - 1) < 1e-8) return 6;
  if (Math.abs(metersPerUnit - 0.001) < 1e-10) return 4;
  return 0;
}

function originalInsUnitsCode(layer: SiteLayer, metersPerUnit: number): number {
  const value = layer.attributes?.cadDrawingInsUnitsCode;
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= 24
    ? value
    : fallbackInsUnitsForMetersPerUnit(metersPerUnit);
}

function sourceHash(layer: SiteLayer): string | null {
  const value = layer.attributes?.cadSourceSha256;
  return typeof value === 'string' && value ? value : null;
}

export interface CadDxfExportGroup {
  sourceSha256: string;
  sourceFile: string;
  layers: SiteLayer[];
}

export function groupCadLayersForExport(layers: readonly SiteLayer[]): CadDxfExportGroup[] {
  const groups = new Map<string, CadDxfExportGroup>();
  for (const layer of layers) {
    if (layer.source !== 'cad-import') continue;
    const hash = sourceHash(layer);
    if (!hash || !georeferenceFromLayer(layer)) continue;
    const existing = groups.get(hash);
    if (existing) {
      existing.layers.push(layer);
    } else {
      const sourceFile = typeof layer.attributes?.cadSourceFile === 'string'
        ? layer.attributes.cadSourceFile
        : 'cad-overlay.dwg';
      groups.set(hash, { sourceSha256: hash, sourceFile, layers: [layer] });
    }
  }
  const physicalPlanningLayers = layers.filter(layer => (
    layer.source === 'scenario-derived'
    && layer.visible !== false
    && layer.attributes?.layoutPhysicalComponent === true
  ));
  if (physicalPlanningLayers.length > 0) {
    for (const group of groups.values()) {
      const reference = group.layers[0];
      if (!reference?.attributes) continue;
      for (const planningLayer of physicalPlanningLayers) {
        group.layers.push({
          ...planningLayer,
          attributes: {
            ...reference.attributes,
            ...planningLayer.attributes,
            cadLayer: `GEN TEST-FIT | ${planningLayer.name}`,
          },
        });
      }
    }
  }
  return [...groups.values()];
}

export function cadLayersToDxf(layers: readonly SiteLayer[]): string {
  if (layers.length === 0) throw new Error('No editable CAD layers are available to export.');
  const georef = georeferenceFromLayer(layers[0]!);
  if (!georef) throw new Error('CAD alignment metadata is missing.');
  const layerNames = [...new Set(layers.map((layer) => dxfLayerName(layer.attributes?.cadLayer ?? layer.name)))];
  const lines: string[] = [
    ...pair(0, 'SECTION'), ...pair(2, 'HEADER'),
    ...pair(999, 'SOURCE-COORDINATE EDIT OVERLAY: insert/reference this DXF into the original DWG to retain its GEODATA/CRS.'),
    ...pair(9, '$ACADVER'), ...pair(1, 'AC1024'),
    ...pair(9, '$INSUNITS'), ...pair(70, originalInsUnitsCode(layers[0]!, georef.metersPerUnit)),
    ...pair(0, 'ENDSEC'),
    ...pair(0, 'SECTION'), ...pair(2, 'TABLES'),
    ...pair(0, 'TABLE'), ...pair(2, 'LAYER'), ...pair(70, layerNames.length),
  ];
  for (const name of layerNames) {
    lines.push(...pair(0, 'LAYER'), ...pair(2, name), ...pair(70, 0), ...pair(62, 7), ...pair(6, 'CONTINUOUS'));
  }
  lines.push(...pair(0, 'ENDTAB'), ...pair(0, 'ENDSEC'), ...pair(0, 'SECTION'), ...pair(2, 'ENTITIES'));

  for (const layer of layers) {
    const layerGeoref = georeferenceFromLayer(layer);
    if (!layerGeoref) continue;
    const name = dxfLayerName(layer.attributes?.cadLayer ?? layer.name);
    const color = layer.attributes?.cadColorIndex;
    const colorPairs = typeof color === 'number' && Number.isInteger(color) && color >= 1 && color <= 255
      ? pair(62, color)
      : [];
    const coordinates = layer.geometry.coordinates.map((point) => latLngToCadPoint(point, layerGeoref));
    if (layer.geometry.type === 'point' && coordinates[0]) {
      lines.push(
        ...pair(0, 'POINT'), ...pair(8, name), ...colorPairs,
        ...pair(10, dxfNumber(coordinates[0].x)), ...pair(20, dxfNumber(coordinates[0].y)), ...pair(30, 0),
      );
      continue;
    }
    const minimum = layer.geometry.type === 'polygon' ? 3 : 2;
    if (coordinates.length < minimum) continue;
    const vertices = layer.geometry.type === 'polygon'
      && coordinates.length > 3
      && coordinates[0]!.x === coordinates.at(-1)!.x
      && coordinates[0]!.y === coordinates.at(-1)!.y
      ? coordinates.slice(0, -1)
      : coordinates;
    lines.push(
      ...pair(0, 'LWPOLYLINE'), ...pair(8, name), ...colorPairs,
      ...pair(90, vertices.length), ...pair(70, layer.geometry.type === 'polygon' ? 1 : 0),
    );
    for (const vertex of vertices) {
      lines.push(...pair(10, dxfNumber(vertex.x)), ...pair(20, dxfNumber(vertex.y)));
    }
  }
  lines.push(...pair(0, 'ENDSEC'), ...pair(0, 'EOF'));
  return `${lines.join(DXF_LINE_ENDING)}${DXF_LINE_ENDING}`;
}

function triggerDownload(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
}

export async function downloadCadLayersDxf(layers: readonly SiteLayer[]): Promise<number> {
  const groups = groupCadLayersForExport(layers);
  if (groups.length === 0) throw new Error('No editable CAD layers are available to export.');
  if (groups.length === 1) {
    const group = groups[0]!;
    triggerDownload(
      new Blob([cadLayersToDxf(group.layers)], { type: 'application/dxf' }),
      `${outputBaseName(group.sourceFile)}-source-coordinates-edited.dxf`,
    );
    return 1;
  }

  const zip = new JSZip();
  for (const group of groups) {
    zip.file(`${outputBaseName(group.sourceFile)}-${group.sourceSha256.slice(0, 8)}-source-coordinates-edited.dxf`, cadLayersToDxf(group.layers));
  }
  const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });
  triggerDownload(blob, 'cad-overlays-edited-dxf.zip');
  return groups.length;
}
